
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { useAuth } from './context/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import PlaceholderPage from './pages/PlaceholderPage';
import ArticleCreation from './pages/ArticleCreation';
import VendorManagement from './pages/VendorManagement';
import VendorFollowUp from './pages/VendorFollowUp';
import CodeToCodePurpose from './pages/CodeToCodePurpose';
import DailyVendorFollowupReport from './pages/DailyVendorFollowupReport';
import ManualHeadOfficeTransaction from './pages/ManualHeadOfficeTransaction';
import TransactionTypeManagement from './pages/TransactionTypeManagement';
import DailyHeadOfficeTransactionReport from './pages/DailyHeadOfficeTransactionReport';
import ApplicationCodeManagement from './pages/ApplicationCodeManagement';
import ApplicationCodeEntry from './pages/ApplicationCodeEntry';
import HardcopySheetManagement from './pages/HardcopySheetManagement';
import MultiHardcopySheet from './pages/MultiHardcopySheet';
import MultiHardcopySheetReport from './pages/MultiHardcopySheetReport';
import HardcopySheetReport from './pages/HardcopySheetReport';
import Datacenter from './pages/DataCenter';
import RoleMaster from './pages/RoleMaster';
import PageMaster from './pages/PageMaster';
import UserPermissions from './pages/UserPermissions';
import Users from './pages/Users';
import TaskDetails from './pages/TaskDetails';
import TaskManagement from './pages/TaskManagement';
import RestoreBin from './pages/RestoreBin';
import Chat from './pages/Chat';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import LoginHistory from './pages/LoginHistory';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route 
        path="/dashboard" 
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } 
      />
      <Route
        path="/articles"
        element={
          <PrivateRoute>
            <ArticleCreation />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <PrivateRoute>
            <VendorManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendor-followup"
        element={
          <PrivateRoute>
            <VendorFollowUp />
          </PrivateRoute>
        }
      />
      <Route
        path="/manual-head-office-transaction"
        element={
          <PrivateRoute>
            <ManualHeadOfficeTransaction />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendor-followup-report"
        element={
          <PrivateRoute>
            <DailyVendorFollowupReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/head-office-report"
        element={
          <PrivateRoute>
            <DailyHeadOfficeTransactionReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/code-purpose"
        element={
          <PrivateRoute>
            <CodeToCodePurpose />
          </PrivateRoute>
        }
      />
      <Route
        path="/transaction-types"
        element={
          <PrivateRoute>
            <TransactionTypeManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/rolemaster"
        element={
          <PrivateRoute>
            <RoleMaster />
          </PrivateRoute>
        }
      />
      <Route
        path="/pagemaster"
        element={
          <PrivateRoute>
            <PageMaster />
          </PrivateRoute>
        }
      />
      <Route
        path="/user-permissions"
        element={
          <PrivateRoute>
            <UserPermissions />
          </PrivateRoute>
        }
      />
      <Route
        path="/user-permissions/:id"
        element={
          <PrivateRoute>
            <UserPermissions />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <Users />
          </PrivateRoute>
        }
      />
      <Route
        path="/applications"
        element={
          <PrivateRoute>
            <ApplicationCodeManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/applications/add"
        element={
          <PrivateRoute>
            <ApplicationCodeEntry />
          </PrivateRoute>
        }
      />
      <Route
        path="/applications/:id/edit"
        element={
          <PrivateRoute>
            <ApplicationCodeEntry />
          </PrivateRoute>
        }
      />
      <Route
        path="/hardcopy-sheet"
        element={
          <PrivateRoute>
            <HardcopySheetManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/multi-hardcopy-sheet"
        element={
          <PrivateRoute>
            <MultiHardcopySheet />
          </PrivateRoute>
        }
      />
      <Route
        path="/multi-hardcopy-sheet-report"
        element={
          <PrivateRoute>
            <MultiHardcopySheetReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/hardcopy-sheet-report"
        element={
          <PrivateRoute>
            <HardcopySheetReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/datacenter"
        element={
          <PrivateRoute>
            <Datacenter />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PrivateRoute>
            <TaskManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/:id"
        element={
          <PrivateRoute>
            <TaskDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/requests"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Requests" />
          </PrivateRoute>
        }
      />
      <Route
        path="/edit-requests"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Edit Requests" />
          </PrivateRoute>
        }
      />
      <Route
        path="/rtgs"
        element={
          <PrivateRoute>
            <PlaceholderPage title="RTGS Details" />
          </PrivateRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Calendar" />
          </PrivateRoute>
        }
      />
      <Route
        path="/grid"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Grid" />
          </PrivateRoute>
        }
      />
      <Route
        path="/bank-master"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Bank Master" />
          </PrivateRoute>
        }
      />
      <Route
        path="/head-master"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Head Master" />
          </PrivateRoute>
        }
      />
      <Route
        path="/tax-master"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Tax Master" />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/new"
        element={
          <PrivateRoute>
            <PlaceholderPage title="Create New" />
          </PrivateRoute>
        }
      />
      <Route
        path="/restore"
        element={
          <PrivateRoute>
            <RestoreBin />
          </PrivateRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <PrivateRoute>
            <Chat />
          </PrivateRoute>
        }
      />
      <Route
        path="/login-history"
        element={
          <PrivateRoute>
            <LoginHistory />
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

export default App;
